import { useEffect } from 'react'
import {
  armNavigationGuard,
  disarmNavigationGuard,
} from '@/utils/navigationGuard'

const DEFAULT_MESSAGE =
  'An operation is still running. Leaving this page now may leave it incomplete.'

/**
 * Warns before the user leaves the page while `active` is true.
 *
 * Covers both escape routes that exist under <BrowserRouter>:
 * - `beforeunload` for tab/window close, reload, and navigation to an external URL
 * - the shared navigation guard for browser back/forward, which never raise `beforeunload`
 *
 * In-app <Link>/navigate() calls cannot be intercepted without a React Router data router,
 * so callers must block those separately. A modal dialog is enough.
 */
export function useNavigationWarning(active: boolean, message: string = DEFAULT_MESSAGE) {
  useEffect(() => {
    if (!active) return

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Browsers show their own generic text, but a non-empty returnValue is still
      // required by older engines for the prompt to appear at all.
      e.returnValue = message
      return message
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    armNavigationGuard(message)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      disarmNavigationGuard()
    }
  }, [active, message])
}
